import React, { ChangeEvent, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { StatusCode } from "api/protocols";
import { showError, showSuccess } from "redux/actions/ui";
import { useAppDispatch } from "hooks/redux";

import Page from "components/page/Page";
import Card from "components/card/Card";
import Form from "components/form/Form";
import FormLayout from "components/form/FormLayout";
import { handleChangeEventInput, unsavedChangesExist } from "helpers/Helpers";
import Input from "components/form/input/Input";
import TextField from "components/form/textField/TextField";
import {
  GetAccommodationBuildings,
  IPutAccommodationBuilding,
  PutAccommodationBuilding,
} from "api/rpc/2024-04/facilityAdmin/accommodation/building";

export function BuildingEdit() {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { buildingId }: any = useParams();

  const [building, setBuildingState] = useState<IPutAccommodationBuilding>({
    id: undefined,
    title: "",
    code: "",
    description: "",
  });

  const [buildingStateBeforeChanges, setBuildingStateBeforeChanges] = useState<IPutAccommodationBuilding>(undefined);

  useEffect(() => {
    void loadBuilding();
  }, []);

  async function loadBuilding() {
    const numericBuildingId = Number(buildingId);
    const buildingRes = await GetAccommodationBuildings({ id: numericBuildingId }, true);

    if (buildingRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading location / building")));
      return;
    }

    const building = buildingRes?.data?.[0];

    setBuildingState(prevState => ({
      ...prevState,
      id: building?.id,
      title: building?.title,
      code: building?.code,
      description: building?.description ?? "",
    }));

    setBuildingStateBeforeChanges(prevState => ({
      ...prevState,
      id: building?.id,
      title: building?.title,
      code: building?.code,
      description: building?.description ?? "",
    }));
  }

  async function updateAccommodationBuilding() {
    const buildingRes = await PutAccommodationBuilding(
      {
        id: Number(buildingId),
        title: building?.title,
        code: building?.code,
        description: building?.description,
      },
      true,
    );

    if (buildingRes.status !== StatusCode.OK) {
      dispatch(showError("Error updating building"));
      return;
    }

    dispatch(showSuccess(t("Successfully updated building")));
    setBuildingStateBeforeChanges(building);
  }

  return (
    <Page
      title={"Edit building or location"}
      subtitle={"Enter key details for your building or location"}
      breadcrumbs={[
        { label: t("Accommodations"), url: "/admin/accommodation" },
        { label: t("Property Configuration"), url: "/admin/accommodation/configuration/locations" },
        { label: t("Locations"), url: "/admin/accommodation/configuration/locations" },
      ]}
      notificationBarProps={{
        isVisible: unsavedChangesExist(building, buildingStateBeforeChanges),
        onAction: () => updateAccommodationBuilding(),
        onCancel: () => setBuildingState(buildingStateBeforeChanges),
      }}
    >
      <Form>
        <Card title="Location Details">
          <div style={{ paddingLeft: "24px", paddingRight: "24px" }}>
            <hr style={{ marginBottom: "0px" }} />
          </div>
          <Card.Section>
            <FormLayout>
              <FormLayout.Group>
                <Input
                  value={building?.title}
                  label={t("Name of building or location")}
                  id="title"
                  onChange={e => handleChangeEventInput(e, building, setBuildingState)}
                  placeholder={t("Enter building or location name")}
                />

                <Input
                  value={building?.code}
                  label={t("Code")}
                  id="code"
                  onChange={e => handleChangeEventInput(e, building, setBuildingState)}
                  placeholder={t("Building Code")}
                />
              </FormLayout.Group>

              <FormLayout.Group>
                <div>
                  <TextField
                    label={t("Description")}
                    value={building?.description}
                    id="description"
                    onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
                      handleChangeEventInput(e, building, setBuildingState)
                    }
                    maxLength={300}
                    characterCountBottom
                    rows={5}
                    placeholder={t("Enter location or building description")}
                  />
                </div>
              </FormLayout.Group>
            </FormLayout>
          </Card.Section>
        </Card>
      </Form>
    </Page>
  );
}
